/**
 * Las listas pequeñas de la clínica en el Catálogo de cirugías (V2, 28/9/2026).
 *
 *  · Productos del mapa facial: qué se marca en el mapa facial del paciente.
 *  · Filtro del registro quirúrgico: las opciones del filtro «Intervención».
 *  · Lugares del quirófano (cuál es la propia clínica), las clínicas que
 *    admiten un «otro cirujano» a mano en el parte y las clínicas del
 *    planificador antiguo de quirófano: listas CERRADAS, el servidor comprueba
 *    lo que se guarda contra ellas (antes eran listas escritas en el código).
 *
 * Antes venían escritas en la pantalla. Las ve todo el que entra al Catálogo;
 * solo un administrador las cambia (el servidor lo impone y apunta el cambio
 * en el rastro). Ver `backend/services/listas_clinica.py`.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import {
  CLINICAS_OTRO_CIRUJANO, CLINICAS_QUIROFANO, INTERVENCIONES_REGISTRO_QX, LUGARES_QUIROFANO,
  PRODUCTOS_MAPA_FACIAL, olvidarListas, urlLista,
} from '../../lib/listasClinica';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const comas = (lista) => (Array.isArray(lista) ? lista : []).join(', ');
const deComas = (texto) => String(texto || '').split(',').map((t) => t.trim()).filter(Boolean);

function mover(lista, i, paso) {
  const j = i + paso;
  if (j < 0 || j >= lista.length) return lista;
  const copia = [...lista];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
}

/** Carga y guarda una lista; `aFilas`/`aValores` traducen a lo que se edita. */
function useListaEditable(clave, aFilas, aValores) {
  const [filas, setFilas] = useState([]);
  const [meta, setMeta] = useState(null);
  const [estado, setEstado] = useState('cargando');
  const [guardando, setGuardando] = useState(false);
  const [cambiada, setCambiada] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get(urlLista(clave))
      .then((r) => {
        if (!vivo) return;
        setFilas(aFilas(Array.isArray(r?.data?.valores) ? r.data.valores : []));
        setMeta(r?.data || null);
        setEstado('lista');
      })
      .catch((e) => { if (vivo) setEstado(mensajeError(e, 'no se ha podido leer')); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const cambiar = (nuevas) => { setFilas(nuevas); setCambiada(true); };
  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put(urlLista(clave), { valores: aValores(filas) });
      setFilas(aFilas(Array.isArray(r?.data?.valores) ? r.data.valores : []));
      setMeta(r?.data || null);
      setCambiada(false);
      olvidarListas();
      toast.success('Guardado');
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar'));
    } finally {
      setGuardando(false);
    }
  };
  return { filas, cambiar, meta, estado, guardar, guardando, cambiada };
}

function Pie({ meta, admin, guardar, guardando, cambiada, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 mt-3">
      <span className="text-[11px] text-muted-foreground">
        {meta?.actualizado_por ? `Último cambio: ${meta.actualizado_por}` : ''}
      </span>
      {admin && (
        <Button size="sm" onClick={guardar} disabled={guardando || !cambiada}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid={`${testid}-guardar`}>
          <Save className="w-4 h-4 mr-1.5" /> Guardar
        </Button>
      )}
    </div>
  );
}

function Estado({ estado }) {
  if (estado === 'cargando') return <p className="text-sm text-muted-foreground">Cargando…</p>;
  return <p className="text-sm text-red-700" role="alert">No se ha podido leer la lista: {estado}.</p>;
}

// ---------------------------------------------------------------- productos
const aFilasProductos = (valores) => valores.map((p) => ({
  clave: p.clave, nombre: p.nombre || '', corto: p.corto || '', color: p.color || '#64748b',
  activo: p.activo !== false, antiguas: comas(p.claves_antiguas), nueva: false,
}));
const aValoresProductos = (filas) => filas.map((f) => ({
  ...(f.nueva ? {} : { clave: f.clave }),
  nombre: f.nombre, corto: f.corto, color: f.color, activo: f.activo,
  claves_antiguas: deComas(f.antiguas),
}));

export function ProductosMapaFacialEditor({ admin }) {
  const l = useListaEditable(PRODUCTOS_MAPA_FACIAL, aFilasProductos, aValoresProductos);
  const set = (i, k, v) => l.cambiar(l.filas.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const quitar = (i) => {
    const f = l.filas[i];
    if (!f.nueva && !window.confirm(`¿Quitar «${f.nombre}»? Sus puntos se seguirán viendo, en gris. `
      + 'Si ya no se usa, mejor desactívalo.')) return;
    l.cambiar(l.filas.filter((_, j) => j !== i));
  };
  const nuevo = () => l.cambiar([...l.filas, {
    clave: '', nombre: '', corto: '', color: '#64748b', activo: true, antiguas: '', nueva: true,
  }]);
  return (
    <section className="cjo-card p-4 mt-5" data-testid="catalogo-productos-mapa">
      <h2 className="text-sm font-semibold text-[#13231E]">Productos del mapa facial</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Lo que se marca en el mapa facial del paciente. El primero activo sale elegido al abrir el
        mapa. Uno inactivo no se ofrece, pero sus puntos se siguen viendo. «Claves antiguas»: lo que
        guardaban los puntos de antes y hoy cuenta como este producto.
      </p>
      {l.estado !== 'lista' ? <Estado estado={l.estado} /> : (
        <>
          {l.filas.length === 0 && (
            <p className="text-sm text-muted-foreground mb-2" data-testid="catalogo-productos-vacio">
              No hay productos: en el mapa facial los puntos se marcan sin producto.
            </p>
          )}
          <div className="space-y-2">
            {l.filas.map((f, i) => (
              <div key={f.clave || `nuevo-${i}`} className="flex flex-wrap items-center gap-2"
                data-testid="catalogo-producto-fila">
                <input type="color" value={f.color} disabled={!admin} aria-label="Color"
                  onChange={(e) => set(i, 'color', e.target.value)} className="w-8 h-8 rounded" />
                <Input className="w-44" value={f.nombre} disabled={!admin} placeholder="Nombre"
                  onChange={(e) => set(i, 'nombre', e.target.value)} data-testid="catalogo-producto-nombre" />
                <Input className="w-20" value={f.corto} disabled={!admin} placeholder="Abrev."
                  onChange={(e) => set(i, 'corto', e.target.value)} />
                <Input className="w-40" value={f.antiguas} disabled={!admin} placeholder="Claves antiguas"
                  onChange={(e) => set(i, 'antiguas', e.target.value)} />
                <label className="text-xs flex items-center gap-1">
                  <input type="checkbox" checked={f.activo} disabled={!admin}
                    onChange={(e) => set(i, 'activo', e.target.checked)} /> activo
                </label>
                {f.clave && <span className="text-[11px] font-mono text-muted-foreground">{f.clave}</span>}
                {admin && (
                  <span className="ml-auto flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => l.cambiar(mover(l.filas, i, -1))}
                      aria-label="Subir"><ArrowUp className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => l.cambiar(mover(l.filas, i, 1))}
                      aria-label="Bajar"><ArrowDown className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => quitar(i)}
                      aria-label="Quitar"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </span>
                )}
              </div>
            ))}
          </div>
          {admin && (
            <Button size="sm" variant="outline" className="mt-2" onClick={nuevo}
              data-testid="catalogo-productos-nuevo">
              <Plus className="w-4 h-4 mr-1" /> Añadir producto
            </Button>
          )}
          <Pie meta={l.meta} admin={admin} guardar={l.guardar} guardando={l.guardando}
            cambiada={l.cambiada} testid="catalogo-productos" />
        </>
      )}
    </section>
  );
}

// ------------------------------------------------------------ intervenciones
const aFilasIntervenciones = (valores) => valores.map((v) => ({
  etiqueta: v.etiqueta || '', palabras: comas(v.palabras), fija: v.fija === true,
}));
const aValoresIntervenciones = (filas) => filas.map((f) => ({
  etiqueta: f.etiqueta, palabras: deComas(f.palabras), fija: !!f.fija,
}));

export function FiltroRegistroQxEditor({ admin }) {
  const l = useListaEditable(INTERVENCIONES_REGISTRO_QX, aFilasIntervenciones, aValoresIntervenciones);
  const set = (i, k, v) => l.cambiar(l.filas.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  return (
    <section className="cjo-card p-4 mt-5" data-testid="catalogo-filtro-registro-qx">
      <h2 className="text-sm font-semibold text-[#13231E]">Filtro del registro quirúrgico</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Las opciones del filtro «Cirugía» del registro quirúrgico. Una opción sale siempre si está
        marcada como fija; si no, cuando alguna intervención del mes contiene una de sus palabras
        (separadas por comas; sin distinguir mayúsculas).
      </p>
      {l.estado !== 'lista' ? <Estado estado={l.estado} /> : (
        <>
          {l.filas.length === 0 && (
            <p className="text-sm text-muted-foreground mb-2" data-testid="catalogo-filtro-vacio">
              Sin opciones: en el registro quirúrgico solo se puede buscar por texto.
            </p>
          )}
          <div className="space-y-2">
            {l.filas.map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid="catalogo-filtro-fila">
                <Input className="w-48" value={f.etiqueta} disabled={!admin} placeholder="Opción"
                  onChange={(e) => set(i, 'etiqueta', e.target.value)} data-testid="catalogo-filtro-etiqueta" />
                <Input className="flex-1 min-w-[12rem]" value={f.palabras} disabled={!admin}
                  placeholder="Palabras que la reconocen"
                  onChange={(e) => set(i, 'palabras', e.target.value)} data-testid="catalogo-filtro-palabras" />
                <label className="text-xs flex items-center gap-1">
                  <input type="checkbox" checked={f.fija} disabled={!admin}
                    onChange={(e) => set(i, 'fija', e.target.checked)} /> siempre
                </label>
                {admin && (
                  <span className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => l.cambiar(mover(l.filas, i, -1))}
                      aria-label="Subir"><ArrowUp className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => l.cambiar(mover(l.filas, i, 1))}
                      aria-label="Bajar"><ArrowDown className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive"
                      onClick={() => l.cambiar(l.filas.filter((_, j) => j !== i))}
                      aria-label="Quitar"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </span>
                )}
              </div>
            ))}
          </div>
          {admin && (
            <Button size="sm" variant="outline" className="mt-2" data-testid="catalogo-filtro-nuevo"
              onClick={() => l.cambiar([...l.filas, { etiqueta: '', palabras: '', fija: false }])}>
              <Plus className="w-4 h-4 mr-1" /> Añadir opción
            </Button>
          )}
          <Pie meta={l.meta} admin={admin} guardar={l.guardar} guardando={l.guardando}
            cambiada={l.cambiada} testid="catalogo-filtro" />
        </>
      )}
    </section>
  );
}

// ------------------------------------------------------ listas de nombres
const aFilasNombres = (valores) => valores.map((v) => ({
  nombre: v.nombre || '', propio: v.propio === true,
}));

/** Una lista cerrada de nombres (y, con `conPropio`, cuál es la propia clínica). */
function NombresEditor({
  admin, clave, titulo, explicacion, vacio, testid, conPropio = false, etiquetaNuevo,
}) {
  const aValores = (filas) => filas.map((f) => (conPropio
    ? { nombre: f.nombre, propio: !!f.propio } : { nombre: f.nombre }));
  const l = useListaEditable(clave, aFilasNombres, aValores);
  const set = (i, k, v) => l.cambiar(l.filas.map((f, j) => {
    if (j === i) return { ...f, [k]: v };
    // Solo uno puede ser la propia clínica.
    return k === 'propio' && v ? { ...f, propio: false } : f;
  }));
  return (
    <section className="cjo-card p-4 mt-5" data-testid={testid}>
      <h2 className="text-sm font-semibold text-[#13231E]">{titulo}</h2>
      <p className="text-xs text-muted-foreground mb-3">{explicacion}</p>
      {l.estado !== 'lista' ? <Estado estado={l.estado} /> : (
        <>
          {l.filas.length === 0 && (
            <p className="text-sm text-muted-foreground mb-2" data-testid={`${testid}-vacio`}>{vacio}</p>
          )}
          <div className="space-y-2">
            {l.filas.map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${testid}-fila`}>
                <Input className="w-64" value={f.nombre} disabled={!admin} placeholder="Nombre"
                  onChange={(e) => set(i, 'nombre', e.target.value)} data-testid={`${testid}-nombre`} />
                {conPropio && (
                  <label className="text-xs flex items-center gap-1">
                    <input type="radio" name={`${testid}-propio`} checked={f.propio} disabled={!admin}
                      onChange={(e) => set(i, 'propio', e.target.checked)}
                      data-testid={`${testid}-propio`} /> la propia clínica
                  </label>
                )}
                {admin && (
                  <span className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => l.cambiar(mover(l.filas, i, -1))}
                      aria-label="Subir"><ArrowUp className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => l.cambiar(mover(l.filas, i, 1))}
                      aria-label="Bajar"><ArrowDown className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive"
                      onClick={() => l.cambiar(l.filas.filter((_, j) => j !== i))}
                      aria-label="Quitar"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </span>
                )}
              </div>
            ))}
          </div>
          {admin && (
            <Button size="sm" variant="outline" className="mt-2" data-testid={`${testid}-nuevo`}
              onClick={() => l.cambiar([...l.filas, { nombre: '', propio: false }])}>
              <Plus className="w-4 h-4 mr-1" /> {etiquetaNuevo}
            </Button>
          )}
          <Pie meta={l.meta} admin={admin} guardar={l.guardar} guardando={l.guardando}
            cambiada={l.cambiada} testid={testid} />
        </>
      )}
    </section>
  );
}

export function LugaresQuirofanoEditor({ admin }) {
  return (
    <NombresEditor admin={admin} clave={LUGARES_QUIROFANO} conPropio
      titulo="Lugares del quirófano" testid="catalogo-lugares-qx" etiquetaNuevo="Añadir lugar"
      explicacion={'Dónde puede ser el quirófano de un día: se elige al dar la primera cita del día '
        + 'o en la barra del día de la agenda. Marca cuál es la propia clínica: sus mensajes llevan '
        + 'la dirección de Configuración y es el lugar de la agenda de externos. Quitar uno no '
        + 'cambia los días que ya lo tienen.'}
      vacio="Sin lugares: no se puede fijar dónde es el quirófano de un día." />
  );
}

export function ClinicasOtroCirujanoEditor({ admin }) {
  return (
    <NombresEditor admin={admin} clave={CLINICAS_OTRO_CIRUJANO}
      titulo="Clínicas que admiten «otro cirujano»" testid="catalogo-clinicas-otro"
      etiquetaNuevo="Añadir clínica"
      explicacion={'Con estas clínicas de procedencia, el parte de quirófano admite un cirujano '
        + 'escrito a mano que no está dado de alta (cuenta como externo). Escribe el nombre tal y '
        + 'como está en las clínicas de procedencia del Catálogo QX.'}
      vacio="Ninguna: en el parte solo se eligen cirujanos dados de alta." />
  );
}

export function ClinicasQuirofanoEditor({ admin }) {
  return (
    <NombresEditor admin={admin} clave={CLINICAS_QUIROFANO}
      titulo="Clínicas del planificador de quirófano" testid="catalogo-clinicas-qx"
      etiquetaNuevo="Añadir clínica"
      explicacion={'La «clínica que la realiza» de las sesiones del planificador antiguo de '
        + 'quirófano. Las sesiones que ya hay se siguen viendo tal cual.'}
      vacio="Sin clínicas: no se pueden crear sesiones nuevas en el planificador antiguo." />
  );
}
